/*
 * Pricing — single source of truth for the Done-it plan families (Time +
 * Project). Components (PricingTable, PricingPlans) all read from here, so plan
 * STRUCTURE (prices, which features exist, ordering) lives in exactly one place.
 *
 * All user-facing COPY is i18n. `name` is a product name (not translated); the
 * tagline + every feature/comingSoon entry is a stable key LEAF resolved at
 * render via `t("pricing:plan.<id>.tagline" | ".feature.<leaf>" | ".comingSoon.<leaf>")`.
 * Authoring a new plan/feature = a content leaf here + a catalog entry per locale;
 * a missing one renders raw and the i18n-html-gate fails the build (E9 backstop).
 */

import { CMS_PRICING } from "./_generated/cms-pricing";

/**
 * Signup-funnel entry point, re-exported for the pricing components.
 *
 * The value is the LOCALE-RELATIVE funnel path (`/plan`), owned by
 * `config/entrypoints.ts` — pass it to a DS `Button`/`LocaleLink`, which applies
 * the active locale prefix; never build an absolute URL from it. It used to be a
 * hardcoded `https://stage.done-it.app/en/plan` at 16 call sites, which threw
 * every market at the INTERNATIONAL funnel (a Belgian reading `/nl/pricing` in
 * euros landed on the English `/en/plan`) and would have pointed production at
 * the staging host after the domain cutover. DIT-399 / DIT-264 QA.
 */
export { TRIAL_PATH as TRIAL_URL } from "../config/entrypoints";

export type Plan = {
  /** i18n key stem: `pricing:plan.<id>.*`. */
  id: string;
  /** Product name — verbatim across locales, not translated. */
  name: string;
  free?: boolean;
  monthly?: { base: number; user: number };
  // `admins` (a count of free administrator seats on the Project plans) was
  // removed on 2026-07-30: the client dropped the "2 admins included"
  // distinction, so every user is a paid seat on every plan.
  /** Feature key leaves → `pricing:plan.<id>.feature.<leaf>`. */
  features: string[];
  /** Coming-soon key leaves → `pricing:plan.<id>.comingSoon.<leaf>`. */
  comingSoon?: string[];
  checkinWork?: boolean; // Belgium only
  popular?: boolean;
};

export const TIME_PLANS: Plan[] = [
  {
    id: "timeZero", name: "Time Zero", free: true,
    features: ["mobileDesktop", "upTo4Users", "upTo5Locations", "holidayAbsenceOvertime", "registrations50"],
  },
  {
    id: "timeBasic", name: "Time Basic", monthly: { base: 15, user: 2.99 }, popular: true,
    features: ["everythingZeroUnlimited", "upTo10Locations", "irregularClockMap", "expenseManagement", "dataStorage5y"],
  },
  {
    id: "timePro", name: "Time Pro", monthly: { base: 15, user: 4.99 },
    features: ["everythingBasic", "unlimitedLocations", "openApi", "employeeDocuments", "navigationSites", "schedulingPlanning"],
    comingSoon: ["internalMessaging", "notifications"],
  },
];

export const PROJECT_PLANS: Plan[] = [
  {
    id: "projectBasic", name: "Project Basic", monthly: { base: 55, user: 4.99 },
    features: ["everythingTimeBasic", "projectBudget", "subprojects", "subcontractorMgmt", "extraWorkMaterials"],
    checkinWork: true,
  },
  {
    id: "projectPro", name: "Project Pro", monthly: { base: 55, user: 6.99 },
    features: ["everythingBasicTimePro", "transportProjects", "employeeDocuments", "productivitySuite", "prioritySupport", "schedulingPlanning"],
    comingSoon: ["internalMessaging", "notifications"],
    checkinWork: true,
  },
];

/** Raw plan amount with the 15% annual discount applied when billed annually. */
export function price(n: number, annual: boolean): number {
  return annual ? n * 0.85 : n;
}

/**
 * Locale-correct currency rendering via `Intl.NumberFormat` — handles symbol
 * placement, separators and the NBSP that differ per locale (e.g. `€12.75` in
 * en vs `12,75 €` in fr), so callers never hand-place a symbol. Falls back to a
 * plain `"12.75 EUR"` form if the runtime rejects the currency code.
 */
export function formatMoney(amount: number, currency: string, locale: string): string {
  const code = currency.toUpperCase();
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${code}`;
  }
}

// --- Multi-currency prices, baked at build from the catalog (DIT-264 / S9) ---
// The numbers above are the EUR baseline; `localizedPlans` overlays them with the
// catalog's per-currency amounts (CMS_PRICING, fetched build-time). Until the
// non-EUR amounts are seeded (commercial input via S2d), every market falls back
// to EUR — exactly as the charging side does (S2b).

/** Marketing plan id → catalog Plan.slug (free Time Zero has no catalog price). */
const PLAN_ID_TO_SLUG: Record<string, string> = {
  timeBasic: "time_basic",
  timePro: "time_pro",
  projectBasic: "project_basic",
  projectPro: "project_pro",
};

/**
 * Locale → CountrySite code: the visitor's MARKET IDENTITY, which decides the
 * currency (and, via feature flags, the market-scoped surfaces).
 *
 * Read from the CMS at build (`CMS_PRICING.localeToCountry`, derived from each
 * site's `languages`), so **entering a new market is an admin action, not a code
 * change** — which is the model DIT-264 specifies ("CountrySites are added
 * through the admin as markets are entered").
 *
 * This replaced a hardcoded ternary, `lt → lt : market === "belgium" ? be : int`.
 * It produced the same three answers, but it could not produce a FOURTH: a UK or
 * NZ site created in the admin was invisible here and its visitors were priced as
 * International. It also derived identity from `Market`, which is a two-value
 * CONTENT profile (Belgian-legislation angle or not) — see `LocaleDef.market`.
 * Those are different questions and are no longer answered by the same value.
 *
 * A language belonging to no site — the funnel ships some the marketing site does
 * not (`fr`, `nl-NL`, `en-NZ`) — resolves to the CMS default site (Belgium), which
 * is both the epic's "Default page = Belgium" and the behaviour before this change.
 * A visitor only reaches those by using the funnel's own language switcher; making
 * that carry the market instead of re-deriving it is the remaining step (ADR-0033).
 */
export function countryFor(localeCode: string): string {
  return mappedCountry(localeCode) ?? CMS_PRICING.defaultCountry ?? "be";
}

/**
 * The CountrySite this locale belongs to, or `undefined` when it belongs to NONE.
 *
 * `countryFor` collapses an unclaimed locale onto the default site, which is the
 * right answer on a cold arrival but wrong once a market is already established.
 * The funnel uses this to tell "this locale names a market" from "this locale
 * names none, keep the one I already have" (see `PlanPage`).
 *
 * Every routable locale names a market today (DIT-399 derives the routable set
 * from the same registry the CountrySites cover), so this only returns undefined
 * when a locale goes live in code ahead of its CountrySite in the CMS.
 */
export function mappedCountry(localeCode: string): string | undefined {
  return CMS_PRICING.localeToCountry[localeCode];
}

/**
 * The currency a CountrySite is priced in, per the CMS. Used by the funnel to ASK
 * the catalog for the right price list; what it then DISPLAYS and CHARGES is
 * `catalog.currency`, the effective one the API reports back (ADR-0033 §1) —
 * those differ whenever a market's amounts are not seeded yet.
 */
export function currencyForMarket(country: string | undefined): string {
  return (country && CMS_PRICING.currencyByCountry[country]) || "eur";
}

/**
 * Overlay `plans` with the catalog prices for `country`'s currency, returning the
 * effective currency. Falls back to the baked EUR numbers when the currency has
 * no seeded amounts, so the build never breaks. Rendering of the currency is the
 * caller's job (see `formatMoney`, which is locale-aware).
 */
export function localizedPlans(
  plans: Plan[],
  country: string
): { plans: Plan[]; currency: string } {
  // Prefer the market's own currency; if its amounts aren't seeded yet, fall
  // back to the EUR *catalog* (so every EUR-displaying market stays consistent),
  // and only to the baked numbers when there's no catalog at all.
  const wanted = CMS_PRICING.currencyByCountry[country] || "eur";
  const currency = CMS_PRICING.prices[wanted] ? wanted : "eur";
  const table = CMS_PRICING.prices[currency];
  if (!table) {
    return { plans, currency: "eur" };
  }
  const overlaid = plans.map((p) => {
    const slug = PLAN_ID_TO_SLUG[p.id];
    const cms = slug ? table[slug] : undefined;
    return cms && p.monthly ? { ...p, monthly: { base: cms.base, user: cms.user } } : p;
  });
  return { plans: overlaid, currency };
}
